import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Badge, Row, Col, InputGroup, Nav, Modal, FloatingLabel, Alert, Dropdown } from "react-bootstrap";
import { toast } from 'react-toastify';
import Navbar from "./Navbar";
import PageTitle from "./components/PageTitle";
import { API_ENDPOINTS } from './config/api';
import { useDelegations } from "./contexts/DelegationsContext";
import RichTextEditor from "./components/RichTextEditor";
import UserAvatar from "./components/UserAvatar";

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState(() => {
    // Initialize from URL hash if present
    const hash = window.location.hash.replace('#', '');
    const validFilters = ['all', 'link', 'document', 'note'];
    return validFilters.includes(hash) ? hash : 'all';
  });
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState(localStorage.getItem("role") || "");
  const [userId, setUserId] = useState(localStorage.getItem("userId") || "");
  const { hasModuleAccess } = useDelegations();
  const canDirectorActions = userRole === 'director' || hasModuleAccess('resources');
  
  // Resource form state
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showTypeSelection, setShowTypeSelection] = useState(true);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    type: '',
    link: '',
    note: '',
    noteColor: 'warning',
    keywords: '',
    bulkLinks: '',
    content: ''
  });
  const [resourceError, setResourceError] = useState('');
  const [resourceSuccess, setResourceSuccess] = useState('');
  const [isSubmittingResource, setIsSubmittingResource] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [expandedPinnedNotes, setExpandedPinnedNotes] = useState(new Set());
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);
  const [activePinnedIndex, setActivePinnedIndex] = useState(0);
  const [isHoveringPinnedContent, setIsHoveringPinnedContent] = useState(false);

  // Sidebar sections (director-editable sections in left sidebar)
  const [sidebarSections, setSidebarSections] = useState([]);
  const [sidebarSectionsLoading, setSidebarSectionsLoading] = useState(false);
  const [sidebarSectionsSaving, setSidebarSectionsSaving] = useState(false);
  const [sidebarNoteIndex, setSidebarNoteIndex] = useState({}); // sectionId -> active note index for carousel
  const [showSidebarLinkModal, setShowSidebarLinkModal] = useState(false);
  const [sidebarLinkForm, setSidebarLinkForm] = useState({ sectionId: null, url: '', label: '' });
  const [showSidebarNoteModal, setShowSidebarNoteModal] = useState(false);
  const [sidebarNoteForm, setSidebarNoteForm] = useState({ sectionId: null, text: '', color: 'warning' });
  const [showSidebarDocModal, setShowSidebarDocModal] = useState(false);
  const [sidebarDocForm, setSidebarDocForm] = useState({ sectionId: null, file: null, alternateName: '' });
  const [sidebarDocUploading, setSidebarDocUploading] = useState(false);
  const [showSidebarSectionModal, setShowSidebarSectionModal] = useState(false);
  const [sidebarSectionForm, setSidebarSectionForm] = useState({ sectionId: null, name: '', direction: '', itemType: null });

  const genId = () => `sb_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  useEffect(() => {
    // Get userId from localStorage if not already set
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && !userId) {
      setUserId(storedUserId);
    }
    fetchResources();
  }, []);

  useEffect(() => {
    filterResources();
  }, [resources, searchTerm, filterType]);

  // Fetch sidebar sections (all users see; only directors can edit)
  useEffect(() => {
    let cancelled = false;
    const fetchSidebar = async () => {
      setSidebarSectionsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_ENDPOINTS.RESOURCES}/sidebar-sections`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        if (!cancelled) setSidebarSections(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setSidebarSections([]);
      } finally {
        if (!cancelled) setSidebarSectionsLoading(false);
      }
    };
    fetchSidebar();
    return () => { cancelled = true; };
  }, []);

  const saveSidebarSections = async (sections) => {
    if (!canDirectorActions) return;
    setSidebarSectionsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.RESOURCES}/sidebar-sections`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sections })
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setSidebarSections(Array.isArray(data) ? data : sections);
      toast.success('Sidebar sections saved');
    } catch (e) {
      toast.error('Failed to save sidebar sections');
    } finally {
      setSidebarSectionsSaving(false);
    }
  };

  // Get pinned resources
  const pinnedResources = resources.filter(r => r.pinned);
  
  // Keep activePinnedIndex in bounds when pinned list changes
  useEffect(() => {
    if (pinnedResources.length > 0 && activePinnedIndex >= pinnedResources.length) {
      setActivePinnedIndex(Math.max(0, pinnedResources.length - 1));
    }
  }, [pinnedResources.length, activePinnedIndex]);

  // Auto-rotate pinned posts every 15 seconds (paused when hovering)
  useEffect(() => {
    if (pinnedResources.length > 1 && !isHoveringPinnedContent) {
      const interval = setInterval(() => {
        setActivePinnedIndex((prev) => (prev + 1) % pinnedResources.length);
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [pinnedResources.length, activePinnedIndex, isHoveringPinnedContent]);

  // Update URL hash when filterType changes
  useEffect(() => {
    if (filterType === 'all') {
      // Remove hash for 'all' to keep URL clean
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } else {
      window.location.hash = filterType;
    }
  }, [filterType]);

  // Listen for hash changes (browser back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const validFilters = ['all', 'link', 'document', 'note'];
      if (validFilters.includes(hash)) {
        setFilterType(hash);
      } else if (!hash) {
        setFilterType('all');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.RESOURCES}/public`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      } else {
        setError("Failed to load resources");
      }
    } catch (error) {
      console.error("Failed to fetch resources", error);
      setError("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter(resource => resource.type === filterType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(resource => {
        const titleMatch = resource.title.toLowerCase().includes(searchLower);
        const descriptionMatch = resource.description && resource.description.toLowerCase().includes(searchLower);
        const keywordsMatch = resource.keywords && 
          (Array.isArray(resource.keywords) 
            ? resource.keywords.some(k => k.toLowerCase().includes(searchLower))
            : resource.keywords.toLowerCase().includes(searchLower));
        const noteMatch = resource.note && resource.note.toLowerCase().includes(searchLower);
        const linkMatch = resource.link && resource.link.toLowerCase().includes(searchLower);
        const contentMatch = resource.content && resource.content.toLowerCase().includes(searchLower);
        return titleMatch || descriptionMatch || keywordsMatch || noteMatch || linkMatch || contentMatch;
      });
    }

    setFilteredResources(filtered);
  };

  // Map note color to Bootstrap Alert variant (or custom class for purple)
  const getNoteAlertVariant = (color) => {
    if (!color) return 'warning';
    const map = {
      blue: 'primary',
      yellow: 'warning',
      red: 'danger',
      purple: 'info',
      primary: 'primary',
      warning: 'warning',
      danger: 'danger',
      info: 'info'
    };
    return map[color] || 'warning';
  };

  const getFileIcon = (fileName, mimeType) => {
    if (!fileName && !mimeType) return "bi-file-earmark";
    const ext = fileName ? fileName.split('.').pop().toLowerCase() : '';
    if (mimeType) {
      if (mimeType.includes('pdf')) return "bi-file-earmark-pdf-fill text-danger";
      if (mimeType.includes('word') || mimeType.includes('document')) return "bi-file-earmark-word-fill text-primary";
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return "bi-file-earmark-excel-fill text-success";
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return "bi-file-earmark-ppt-fill text-warning";
      if (mimeType.includes('image')) return "bi-file-earmark-image-fill text-info";
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return "bi-file-earmark-zip-fill text-secondary";
    }
    if (ext === 'pdf') return "bi-file-earmark-pdf-fill text-danger";
    if (['doc', 'docx'].includes(ext)) return "bi-file-earmark-word-fill text-primary";
    if (['xls', 'xlsx'].includes(ext)) return "bi-file-earmark-excel-fill text-success";
    if (['ppt', 'pptx'].includes(ext)) return "bi-file-earmark-ppt-fill text-warning";
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return "bi-file-earmark-image-fill text-info";
    if (['zip', 'rar'].includes(ext)) return "bi-file-earmark-zip-fill text-secondary";
    return "bi-file-earmark";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const normalizeUrl = (url) => {
    if (!url) return url;
    const trimmed = url.trim();
    // If it already has a protocol, return as is
    if (trimmed.match(/^https?:\/\//i)) {
      return trimmed;
    }
    // If it starts with //, add https:
    if (trimmed.startsWith('//')) {
      return 'https:' + trimmed;
    }
    // Otherwise, add https://
    return 'https://' + trimmed;
  };

  const downloadFile = async (resource) => {
    try {
      const token = localStorage.getItem("token");
      const url = resource.webPath.startsWith('http') 
        ? resource.webPath 
        : resource.webPath.startsWith('/uploads/') 
          ? `${API_ENDPOINTS.PROFILE_PICS}${resource.webPath}` 
          : `${API_ENDPOINTS.PROFILE_PICS}/uploads/${resource.webPath}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = resource.originalName || resource.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        alert('Failed to download file. Please try again.');
      }
    } catch (error) {
      console.error("Failed to download file", error);
      alert('Failed to download file. Please try again.');
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Resources" description="Helpful links, documents, and notes" />
        <Navbar pageTitle="Resources" />
        <Container className="mt-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading resources...</p>
          </div>
        </Container>
      </div>
    );
  }

  const getTypeCount = (type) => {
    if (type === 'all') return resources.length;
    return resources.filter(r => r.type === type).length;
  };

  const resetResourceForm = () => {
    setResourceForm({
      title: '',
      description: '',
      type: '',
      link: '',
      note: '',
      noteColor: 'warning',
      keywords: '',
      bulkLinks: '',
      content: ''
    });
    setShowTypeSelection(true);
    setEditingResource(null);
    setExistingFiles([]);
    setRemovedFiles([]);
    setResourceError('');
    setResourceSuccess('');
  };

  const handleTypeSelection = (type) => {
    setResourceForm({
      ...resourceForm,
      type: type,
      link: '',
      note: '',
      noteColor: 'warning',
      bulkLinks: type === 'link' ? resourceForm.bulkLinks : ''
    });
    setShowTypeSelection(false);
  };

  const handleResourceSubmit = async () => {
    setIsSubmittingResource(true);
    setResourceError('');
    setResourceSuccess('');

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setResourceError('You must be logged in');
        return;
      }

      // Validate description length (100 characters including spaces)
      if (resourceForm.description && resourceForm.description.length > 100) {
        setResourceError('Description must be 100 characters or less');
        setIsSubmittingResource(false);
        return;
      }

      const formData = new FormData();
      formData.append('title', resourceForm.title);
      formData.append('description', resourceForm.description || '');
      formData.append('type', resourceForm.type);
      formData.append('keywords', resourceForm.keywords || '');

      if (resourceForm.type === 'link') {
        if (!resourceForm.bulkLinks || !resourceForm.bulkLinks.trim()) {
          setResourceError('Please enter at least one link');
          setIsSubmittingResource(false);
          return;
        }
        formData.append('bulkLinks', resourceForm.bulkLinks);
        formData.append('content', resourceForm.content || '');
      } else if (resourceForm.type === 'note') {
        formData.append('note', resourceForm.note);
        formData.append('noteColor', resourceForm.noteColor || 'warning');
      } else if (resourceForm.type === 'document') {
        formData.append('content', resourceForm.content || '');
        const fileInput = document.getElementById('resource-file-input');
        if (fileInput && fileInput.files && fileInput.files.length > 0) {
          // Append all selected files
          for (let i = 0; i < fileInput.files.length; i++) {
            formData.append('files', fileInput.files[i]);
          }
        } else if (!editingResource) {
          setResourceError('Please select at least one file for document type');
          setIsSubmittingResource(false);
          return;
        }
        
        // If editing, include removed files info
        if (editingResource && removedFiles.length > 0) {
          formData.append('removedFiles', JSON.stringify(removedFiles));
        }
      }

      const url = editingResource 
        ? `${API_ENDPOINTS.RESOURCES}/${editingResource.id}`
        : `${API_ENDPOINTS.RESOURCES}`;
      const method = editingResource ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setResourceSuccess(
          editingResource 
            ? 'Resource updated successfully!' 
            : 'Resource created successfully!'
        );
        setShowResourceModal(false);
        resetResourceForm();
        fetchResources();
        setTimeout(() => setResourceSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setResourceError(errorData.message || 'Failed to save resource');
      }
    } catch (error) {
      setResourceError('Failed to save resource');
    } finally {
      setIsSubmittingResource(false);
    }
  };

  const handleDeleteResource = async (id) => {
    setConfirmMessage('Are you sure you want to delete this resource? This action cannot be undone.');
    setConfirmAction(() => async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_ENDPOINTS.RESOURCES}/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          setResourceSuccess('Resource deleted successfully!');
          fetchResources();
          setTimeout(() => setResourceSuccess(''), 3000);
        } else {
          setError('Failed to delete resource');
        }
      } catch (error) {
        console.error("Failed to delete resource", error);
        setError('Failed to delete resource');
      }
    });
    setShowConfirmModal(true);
  };

  const editResource = (resource) => {
    setEditingResource(resource);
    
    // Get files array - check if resource has files array, otherwise use single file fields
    let files = [];
    if (resource.files && Array.isArray(resource.files) && resource.files.length > 0) {
      files = resource.files;
    } else if (resource.fileName) {
      // Backward compatibility: convert single file to array
      files = [{
        fileName: resource.fileName,
        originalName: resource.originalName,
        filePath: resource.filePath,
        webPath: resource.webPath,
        fileSize: resource.fileSize,
        mimeType: resource.mimeType
      }];
    }
    
    setExistingFiles(files);
    setRemovedFiles([]);
    
    setResourceForm({
      title: resource.title,
      description: resource.description || '',
      type: resource.type,
      link: '',
      note: resource.note || '',
      noteColor: resource.noteColor || 'warning',
      keywords: Array.isArray(resource.keywords) ? resource.keywords.join(', ') : (resource.keywords || ''),
      bulkLinks: resource.type === 'link' ? (resource.link || '') : '',
      content: resource.content || ''
    });
    setShowTypeSelection(false); // Skip type selection when editing
    setShowResourceModal(true);
  };

  const canEditDeleteResource = (resource) => {
    // Directors and attendings can edit/delete any resource
    if (userRole === 'director' || userRole === 'attending' || hasModuleAccess('resources')) {
      return true;
    }
    // Residents can only edit/delete their own resources
    if (userRole === 'resident') {
      return resource.createdBy === parseInt(userId);
    }
    return false;
  };

  const handleTogglePin = async (id) => {
    try {
      const token = localStorage.getItem("token");

      // Frontend guard: enforce max 10 pinned posts
      const currentResource = resources.find(r => r.id === id);
      if (!currentResource) {
        return;
      }

      const currentPinnedCount = resources.filter(r => r.pinned).length;

      // If we're pinning (not unpinning) and already at 10, block and show toast
      if (!currentResource.pinned && currentPinnedCount >= 10) {
        toast.error('You can only pin up to 10 posts.');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.RESOURCES}/${id}/pin`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        // Compute how many will be pinned after this toggle
        const newPinnedCount = currentResource.pinned
          ? Math.max(0, currentPinnedCount - 1)
          : currentPinnedCount + 1;
        const remaining = Math.max(0, 10 - newPinnedCount);

        toast.info(
          remaining === 0
            ? 'You have reached the maximum of 10 pinned posts.'
            : `You can pin ${remaining} more post${remaining === 1 ? '' : 's'}.`
        );

        fetchResources();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || 'Failed to toggle pin status';
        setError(message);
        toast.error(message);
      }
    } catch (error) {
      console.error("Failed to toggle pin", error);
      setError('Failed to toggle pin status');
      toast.error('Failed to toggle pin status');
    }
  };

  return (
    <div>
      <PageTitle title="Resources" description="Helpful links, documents, and notes" />
      <Navbar pageTitle="Resources" />

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row className="align-items-center">
            <Col lg={4} md={4} sm={6} className="col-12  order-1 order-md-1">
              <h6 className="mb-0 fw-semibold py-3">Resources & helpful links</h6>
            </Col>
      
                  <Col lg={8} md={8} sm={12} className="col-12 mb-1 mb-md-0 d-flex flex-row justify-content-md-end justify-content-between align-items-center text-md-end order-2 order-md-3 text-end gap-2">
                       <Form.Group>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Search resources..."
                        value={searchTerm}

                        onChange={(e) => setSearchTerm(e.target.value)}
                      />  
                    </Form.Group>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        resetResourceForm();
                        setShowResourceModal(true);
                      }}
                    >
                      <i className="bi bi-plus-lg me-1" />
                       <span className="d-none d-md-inline">Add Resource</span>
                    </Button>
                  </Col>
          </Row>
        </Container>
      </div>
      <Container className="mt-4">
        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        <Row>
          {/* Main Content Area - col-10 */}
          <Col lg={10}>
            {resourceSuccess && (
              <Alert variant="success" onClose={() => setResourceSuccess('')} dismissible className="mb-4">
                {resourceSuccess}
              </Alert>
            )}

            {/* Mobile Filter - shown on small screens */}
            <div className="d-lg-none mb-4">
              <Form.Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types ({getTypeCount('all')})</option>
                <option value="link">Links ({getTypeCount('link')})</option>
                <option value="document">Documents ({getTypeCount('document')})</option>
                <option value="note">Notes ({getTypeCount('note')})</option>
              </Form.Select>
            </div>

            {/* Pinned Posts Carousel - only when filter is "all" and no search */}
            {pinnedResources.length > 0 && filterType === 'all' && !searchTerm.trim() && (() => {
              const activeResource = pinnedResources[activePinnedIndex];
              if (!activeResource) return null;
              const links = activeResource.link && activeResource.link.includes('\n') 
                ? activeResource.link.split('\n').filter(l => l.trim())
                : activeResource.link ? [activeResource.link.trim()] : [];
              let files = [];
              if (activeResource.files && Array.isArray(activeResource.files) && activeResource.files.length > 0) {
                files = activeResource.files;
              } else if (activeResource.fileName) {
                files = [{
                  fileName: activeResource.fileName,
                  originalName: activeResource.originalName,
                  filePath: activeResource.filePath,
                  webPath: activeResource.webPath,
                  fileSize: activeResource.fileSize,
                  mimeType: activeResource.mimeType
                }];
              }
              const isMultipleLinks = links.length > 1;
              const isMultipleFiles = files.length > 1;
              
              return (
                <Card className="shadow-sm border-0 mb-4" style={{ borderLeft: '4px solid #ffc107' }}>
                  <Card.Body className="p-0">
                    <Row className="g-0">
                      {/* Main Content */}
                      <Col md={9}>
                        <div 
                          className="p-4 d-flex flex-column" 
                          style={{ minHeight: '400px' }}
                          onMouseEnter={() => setIsHoveringPinnedContent(true)}
                          onMouseLeave={() => setIsHoveringPinnedContent(false)}
                        >
                          <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 className="mb-1">{activeResource.title}</h5>
                              {(activeResource.description || activeResource.createdAt) && (
                                <p className="text-muted small mb-2">
                                  {activeResource.description && <span>{activeResource.description}</span>}
                                  {activeResource.createdAt && (
                                    <span className="ms-2">
                                      {activeResource.description && ' · '}
                                      Created {new Date(activeResource.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg={activeResource.type === 'link' ? 'primary' : activeResource.type === 'document' ? 'info' : 'warning'}>
                                {activeResource.type}
                              </Badge>
                              {canDirectorActions && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleTogglePin(activeResource.id)}
                                  title="Unpin post"
                                >
                                  <i className="bi bi-pin-fill"></i>
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Content Display */}
                          {activeResource.content && (
                            <div className="mb-4">
                              <p className="text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                {activeResource.content}
                              </p>
                            </div>
                          )}
                          
                          {/* Links */}
                          {activeResource.type === 'link' && links.length > 0 && (
                            <div className="mb-3">
                              {isMultipleLinks ? (
                                <Row>
                                  {links.map((link, idx) => {
                                    const normalizedLink = normalizeUrl(link);
                                    return (
                                      <Col key={idx} md={6} className="mb-2">
                                        <Button 
                                          variant="primary-subtle"
                                          size="sm"
                                          onClick={() => window.open(normalizedLink, '_blank')}
                                          className="w-100 text-start d-flex flex-row justify-content-between align-items-center"
                                        >
                                          <span><i className="bi bi-link-45deg me-2"></i>{link.trim().length > 50 ? link.trim().substring(0, 50) + '...' : link.trim()}</span>
                                          <i className="bi bi-box-arrow-up-right align-self-end"></i>
                                        </Button>
                                      </Col>
                                    );
                                  })}
                                </Row>
                              ) : (
                                <Button 
                                  variant="primary-subtle"
                                  size="sm"
                                  onClick={() => window.open(normalizeUrl(links[0]), '_blank')}
                                  className="w-100 text-start d-flex flex-row justify-content-between align-items-center"
                                >
                                  <span><i className="bi bi-link-45deg me-2"></i>{links[0].length > 50 ? links[0].substring(0, 50) + '...' : links[0]}</span>
                                  <i className="bi bi-box-arrow-up-right align-self-end"></i>
                                </Button>
                              )}
                            </div>
                          )}
                          
                          {/* Documents */}
                          {activeResource.type === 'document' && files.length > 0 && (
                            <div className="mb-3">
                              {isMultipleFiles ? (
                                <Row>
                                  {files.map((file, idx) => (
                                    <Col key={idx} md={6} className="mb-2 ">
                                      <div className="bg-light border resource-container custom-hover-bg rounded-3 p-2 d-flex flex-row justify-content-between align-items-center">
                                        <p className="mb-0 ms-1 py-2 small">
                                          <i className={`bi ${getFileIcon(file.fileName, file.mimeType)} me-2`}></i>
                                          {file.originalName || file.fileName}
                                          {file.fileSize && (
                                            <span className="ms-2 small text-muted">({formatFileSize(file.fileSize)})</span>
                                          )}
                                        </p>
                                        <Button
                                          variant="light-solid"
                                          size="sm"
                                          onClick={() => downloadFile({ ...activeResource, ...file })}
                                          className="hide-button py-1 px-2"
                                        >
                                          <i className="bi bi-download"></i>
                                        </Button>
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              ) : (
                                <div className="bg-light border resource-container custom-hover-bg rounded-3 p-2 d-flex flex-row justify-content-between align-items-center">
                                  <p className="mb-0 ms-1 py-2 small">
                                    <i className={`bi ${getFileIcon(files[0].fileName, files[0].mimeType)} me-2`}></i>
                                    {files[0].originalName || files[0].fileName}
                                    {files[0].fileSize && (
                                      <span className="ms-2 small text-muted">({formatFileSize(files[0].fileSize)})</span>
                                    )}
                                  </p>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => downloadFile({ ...activeResource, ...files[0] })}
                                    className="hide-button py-1 px-2"
                                  >
                                    <i className="bi bi-download"></i>
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Notes */}
                          {activeResource.type === 'note' && activeResource.note && (
                            <div className="mb-3">
                              {activeResource.note.length > 1200 ? (
                                <>
                                  <Alert variant={getNoteAlertVariant(activeResource.noteColor)} className="mb-2">
                                    <div
                                      className="eval-rich-content"
                                      style={{ lineHeight: '1.6' }}
                                      dangerouslySetInnerHTML={{
                                        __html: expandedPinnedNotes.has(activeResource.id)
                                          ? activeResource.note
                                          : `${activeResource.note.substring(0, 1200)}...`,
                                      }}
                                    />
                                  </Alert>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 mt-2 text-decoration-none fw-semibold"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedPinnedNotes);
                                      if (newExpanded.has(activeResource.id)) {
                                        newExpanded.delete(activeResource.id);
                                      } else {
                                        newExpanded.add(activeResource.id);
                                      }
                                      setExpandedPinnedNotes(newExpanded);
                                    }}
                                  >
                                    {expandedPinnedNotes.has(activeResource.id) ? 'Show less' : 'Show more'}
                                    <i className={`bi ${expandedPinnedNotes.has(activeResource.id) ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`}></i>
                                  </Button>
                                </>
                              ) : (
                                <div
                                  className="text-muted eval-rich-content"
                                  style={{ lineHeight: '1.6' }}
                                  dangerouslySetInnerHTML={{ __html: activeResource.note }}
                                />
                              )}
                            </div>
                          )}
                          </div>
                          
                          {/* Keywords */}
                          {activeResource.keywords && (
                            <div className="mt-3 pt-3 mt-auto">
                              <div className="d-flex flex-wrap gap-1">
                                {(Array.isArray(activeResource.keywords) 
                                  ? activeResource.keywords 
                                  : activeResource.keywords.split(',').map(k => k.trim())).map((keyword, idx) => (
                                  <Badge key={idx} bg="outline-light" className="small">
                                    #{keyword}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>
                      
                      {/* Sidebar */}
                      <Col md={3} className="bg-light" style={{ borderRadius: '0 10px 10px 0'}}>
                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          <h6 className="mb-3 fw-semibold px-3 pt-3">
                            Pinned Posts
                          </h6>
                          {pinnedResources.map((resource, idx) => (
                            <div
                              key={resource.id}
                              className={`mx-1 p-2 rounded-3 cursor-pointer ${idx === activePinnedIndex ? 'bg-white' : ''}`}
                              onClick={() => {
                                setActivePinnedIndex(idx);
                              }}
                              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                              <div className="d-flex align-items-start gap-2">
                                <div className="flex-shrink-0">
                                  {resource.type === 'link' && (
                                    <div className="bg-primary bg-opacity-10 rounded-2 px-2 py-1">
                                      <i className="bi bi-link-45deg text-primary"></i>
                                    </div>
                                  )}
                                  {resource.type === 'document' && (
                                    <div className="bg-info bg-opacity-10 rounded-2 px-2 py-1">
                                      <i className="bi bi-file-earmark text-info"></i>
                                    </div>
                                  )}
                                  {resource.type === 'note' && (
                                    <div className="bg-warning bg-opacity-10 rounded-2 px-2 py-1">
                                      <i className="bi bi-sticky-fill text-warning"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1 small fw-semibold">{resource.title}</h6>
                                  {(resource.description || resource.createdAt) && (
                                    <p className="mb-1 small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.3' }}>
                                      {resource.description && (resource.description.length > 60 ? resource.description.substring(0, 60) + '...' : resource.description)}
                                      {resource.createdAt && (
                                        <span className={resource.description ? ' ms-1' : ''}>
                                          {resource.description && ' · '}
                                          Created {new Date(resource.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              );
            })()}

            <Row>
              <Col lg={3} className="d-none d-md-block">
              <Card className="shadow-sm border-0 custom-sticky-top" style={{ top: '5rem' }}>
                <Card.Header className="border-0 p-3" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                  <h6 className="mb-0 fw-semibold">Filter by Type</h6>
                </Card.Header>
                <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                  <Nav variant="pills"  className="flex-column-reverse flex-md-column mb-3">
                    <Nav.Item>
                      <Nav.Link
                        active={filterType === 'all'}
                        onClick={() => setFilterType('all')}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className="bi bi-grid me-2"></i>
                          All Resources
                        </span>
                        <Badge bg="secondary" className="ms-2">{getTypeCount('all')}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={filterType === 'link'}
                        onClick={() => setFilterType('link')}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className="bi bi-link-45deg me-2"></i>
                          Links
                        </span>
                        <Badge bg="primary" className="ms-2">{getTypeCount('link')}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={filterType === 'document'}
                        onClick={() => setFilterType('document')}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className="bi bi-file-earmark me-2"></i>
                          Documents
                        </span>
                        <Badge bg="info" className="ms-2">{getTypeCount('document')}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={filterType === 'note'}
                        onClick={() => setFilterType('note')}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className="bi bi-sticky-fill me-2"></i>
                          Notes
                        </span>
                        <Badge bg="warning" className="ms-2">{getTypeCount('note')}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <div className="pe-2">
                    <Alert
                      variant="info"
                      className="border-0 mb-0 small"
                    >
                      <i className="bi bi-info-circle me-2"></i>
                      Welcome! Here you can browse, search, and add new resources. Use the filters to narrow your results.
                    </Alert>
                  </div>
                </Card.Body>
              </Card>
              
              </Col>
              <Col lg={9}>
              {/* Resources Column Layout - when carousel shown, list excludes pinned; otherwise list includes all filtered (including pinned) */}
              {(() => {
                const showCarousel = pinnedResources.length > 0 && filterType === 'all' && !searchTerm.trim();
                const listResources = showCarousel ? filteredResources.filter(r => !r.pinned) : filteredResources;
                return listResources.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <Card.Body className="text-center py-5">
                    <i className="bi bi-bookmark-star text-muted mb-3" style={{ fontSize: '3rem' }} />
                    <h5 className="text-muted">No resources found</h5>
                    <p className="text-muted">
                      {searchTerm || filterType !== "all" 
                        ? "Try adjusting your search or filter criteria."
                        : pinnedResources.length > 0
                          ? "No more resources to display."
                          : "No resources have been added yet."}
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {listResources.map((resource) => (
                    <Card key={resource.id} className="shadow-sm border-0 p-2 resource-container">
                      <Card.Body>
                        <Row className="align-items-start">
                          {/*<Col xs="auto">
                            {resource.type === 'link' && (
                              <div className="bg-primary bg-opacity-10 rounded-3 px-3 py-2">
                                <i className="bi bi-link-45deg text-primary" style={{ fontSize: '1.5rem' }}></i>
                              </div>
                            )}
                            {resource.type === 'document' && (() => {
                              // Check if resource has multiple files
                              let fileCount = 0;
                              if (resource.files && Array.isArray(resource.files) && resource.files.length > 0) {
                                fileCount = resource.files.length;
                              } else if (resource.fileName) {
                                // Backward compatibility: single file
                                fileCount = 1;
                              }
                              
                              // Use folder icon if multiple files, otherwise use file icon
                              const iconClass = fileCount > 1 
                                ? "bi-folder-fill text-info" 
                                : getFileIcon(resource.fileName, resource.mimeType);
                              
                              return (
                                <div className="bg-info bg-opacity-10 rounded-3 px-3 py-2">
                                  <i className={`bi ${iconClass}`} style={{ fontSize: '1.5rem' }}></i>
                                </div>
                              );
                            })()}
                            {resource.type === 'note' && (
                              <div className="bg-warning bg-opacity-10 rounded-3 px-3 py-2">
                                <i className="bi bi-sticky-fill text-warning" style={{ fontSize: '1.5rem' }}></i>
                              </div>
                            )}
                          </Col>  */}
                          <Col>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex flex-column">
                              <div className="d-flex flex-row gap-2 align-items-center">
                                <h6 className="mb-0 fw-semibold">{resource.title}</h6>
                                <Badge bg={resource.type === 'link' ? 'primary' : resource.type === 'document' ? 'info' : 'secondary'}>
                                  {resource.type}
                                </Badge>
                              </div>
                              
                            {(resource.description || resource.createdAt) && (
                              <p className="text-muted mb-2 small">
                                {resource.description && <span>{resource.description}</span>}
                                {resource.createdAt && (
                                  <span className={resource.description ? ' ms-2' : ''}>
                                    {resource.description && ' · '}
                                    Created {new Date(resource.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                )}
                              </p>
                            )}

                              </div>
                              <div className="d-flex gap-1">
                                {canDirectorActions && (
                                  <Button
                                    variant={resource.pinned ? "warning" : "light-solid"}
                                    size="sm"
                                    className="hide-button"
                                    onClick={() => handleTogglePin(resource.id)}
                                    title={resource.pinned ? "Unpin" : "Pin to top"}
                                  >
                                    <i className={`bi ${resource.pinned ? 'bi-pin-fill' : 'bi-pin'}`}></i>
                                  </Button>
                                )}
                                {canEditDeleteResource(resource) && (
                                  <>
                                    <Button
                                      variant="light-solid"
                                      size="sm"
                                      onClick={() => editResource(resource)}
                                      title="Edit"
                                      className="hide-button"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button
                                      className="hide-button"
                                      variant="light-solid"
                                      size="sm"
                                      onClick={() => handleDeleteResource(resource.id)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>

                            {resource.type === 'link' && resource.link && (() => {
                              const links = resource.link.includes('\n') 
                                ? resource.link.split('\n').filter(l => l.trim())
                                : [resource.link.trim()];
                              const isMultiple = links.length > 1;
                              
                              return (
                                <div>
                                  {resource.content && (
                                    <div className="mb-2">
                                      <p className="text-muted">
                                        {resource.content}
                                      </p>
                                    </div>
                                  )}
                                  {isMultiple ? (
                                    // Multiple links - display in two columns
                                    <Row className="mb-2">
                                      {links.map((link, idx) => {
                                        const normalizedLink = normalizeUrl(link);
                                        return (
                                          <Col key={idx} md={6} className="mb-2">
                                            <Button 
                                              variant="primary-subtle"
                                              size="sm"
                                              onClick={() => window.open(normalizedLink, '_blank')}
                                              className="w-100 text-start d-flex flex-row justify-content-between align-items-center"
                                            >
                                              <span><i className="bi bi-link-45deg me-2"></i>{link.trim().length > 50 ? link.trim().substring(0, 50) + '...' : link.trim()}</span>
                                              <i className="bi bi-box-arrow-up-right align-self-end"></i>
                                            </Button>
                                          </Col>
                                        );
                                      })}
                                    </Row>
                                  ) : (
                                    // Single link - full width
                                    <div className="mb-2">
                                      <Button 
                                        variant="primary-subtle"
                                        size="sm"
                                        onClick={() => window.open(normalizeUrl(links[0]), '_blank')}
                                        className="w-100 text-start d-flex flex-row justify-content-between align-items-center"
                                      >
                                        <span><i className="bi bi-link-45deg me-2"></i>{links[0].length > 50 ? links[0].substring(0, 50) + '...' : links[0]}</span>
                                        <i className="bi bi-box-arrow-up-right align-self-end"></i>
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {resource.type === 'document' && (() => {
                              // Get files array - check if resource has files array, otherwise use single file fields
                              let files = [];
                              if (resource.files && Array.isArray(resource.files) && resource.files.length > 0) {
                                files = resource.files;
                              } else if (resource.fileName) {
                                // Backward compatibility: convert single file to array
                                files = [{
                                  fileName: resource.fileName,
                                  originalName: resource.originalName,
                                  filePath: resource.filePath,
                                  webPath: resource.webPath,
                                  fileSize: resource.fileSize,
                                  mimeType: resource.mimeType
                                }];
                              }
                              
                              const isMultiple = files.length > 1;
                              
                              return (
                                <>
                                  {resource.content && (
                                    <div className="mb-2">
                                      <p className="text-muted">
                                        {resource.content}
                                      </p>
                                    </div>
                                  )}
                                  {files.length > 0 ? (
                                    isMultiple ? (
                                      // Multiple documents - display in two columns
                                      <Row className="mb-3">
                                        {files.map((file, idx) => (
                                          <Col key={idx} md={6} className="mb-2">
                                            <div className="bg-light border custom-hover-bg rounded-3 p-2 d-flex flex-row justify-content-between align-items-center">
                                              <p className="mb-0 ms-1 py-2 small">
                                                <i className={`bi ${getFileIcon(file.fileName, file.mimeType)} me-2`}></i>
                                                {file.originalName || file.fileName}
                                                {file.fileSize && (
                                                  <span className="ms-2 small text-muted align-self-center">({formatFileSize(file.fileSize)})</span>
                                                )}
                                              </p>
                                              <Button
                                                variant="light-solid"
                                                size="sm"
                                                onClick={() => downloadFile({ ...resource, ...file })}
                                              >
                                                <i className="bi bi-download"></i>
                                              </Button>
                                            </div>
                                          </Col>
                                        ))}
                                      </Row>
                                    ) : (
                                      // Single document - full width
                                      <div className="mb-3">
                                        <div className="bg-light border custom-hover-bg rounded-3 p-2 d-flex flex-row justify-content-between align-items-center">
                                          <p className="mb-0 ms-1 py-2">
                                            <i className={`bi ${getFileIcon(files[0].fileName, files[0].mimeType)} me-2`}></i>
                                            {files[0].originalName || files[0].fileName}
                                            {files[0].fileSize && (
                                              <span className="ms-2 small text-muted align-self-center">({formatFileSize(files[0].fileSize)})</span>
                                            )}
                                          </p>
                                          <Button
                                            variant="light-solid"
                                            size="sm"
                                            onClick={() => downloadFile({ ...resource, ...files[0] })}
                                          >
                                            <i className="bi bi-download"></i>
                                            <span className="d-none d-md-inline ms-1">Download</span>
                                          </Button>
                                        </div>
                                      </div>
                                    )
                                  ) : null}
                                </>
                              );
                            })()}

                            {resource.type === 'note' && resource.note && (
                              <div className="mb-2">
                                {resource.note.length > 360 ? (
                                  <>
                                    <Alert variant={getNoteAlertVariant(resource.noteColor)} className="mb-2 d-flex flex-column justify-content-between align-items-start">
                                      <div
                                        className="eval-rich-content"
                                        style={{ lineHeight: '1.6' }}
                                        dangerouslySetInnerHTML={{
                                          __html: expandedNotes.has(resource.id)
                                            ? resource.note
                                            : `${resource.note.substring(0, 360)}...`,
                                        }}
                                      />
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mt-2 text-decoration-none fw-semibold"
                                        onClick={() => {
                                          const newExpanded = new Set(expandedNotes);
                                          if (newExpanded.has(resource.id)) {
                                            newExpanded.delete(resource.id);
                                          } else {
                                            newExpanded.add(resource.id);
                                          }
                                          setExpandedNotes(newExpanded);
                                        }}
                                      >
                                        {expandedNotes.has(resource.id) ? 'View less' : 'View more'}
                                        <i className={`bi ${expandedNotes.has(resource.id) ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`}></i>
                                      </Button>
                                    </Alert>
                                  </>
                                ) : (
                                  <div
                                    className="text-muted mb-0 eval-rich-content"
                                    style={{ lineHeight: '1.6' }}
                                    dangerouslySetInnerHTML={{ __html: resource.note }}
                                  />
                                )}
                              </div>
                            )}
                            <div className="d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start gap-2 mt-2">
                            {resource.keywords && (
                              <div>
                                <div className="d-flex flex-wrap gap-1">
                                  {(Array.isArray(resource.keywords) 
                                    ? resource.keywords 
                                    : resource.keywords.split(',').map(k => k.trim())).map((keyword, idx) => (
                                    <Badge key={idx} bg="outline-light" className="small">
                                      #{keyword}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                              <div className="small text-muted d-flex align-items-center gap-1 flex-wrap">
                                <span className="small fw-semibold">
                                  {resource.creator?.firstName && resource.creator?.lastName
                                    ? `${resource.creator.firstName} ${resource.creator.lastName}`
                                    : 'Unknown'}
                                </span> 
                                <UserAvatar user={resource.creator} size={24} alt="" />

                              </div>
                          </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              );
              })()}
              </Col>
            </Row>
          </Col>
          {/* Sidebar - sections (director can add/edit); all users see content */}
          <Col lg={2} className="d-none d-lg-block mb-4">
            <Card className="border-0 bg-white shadow-sm p-0 py-2 mb-3">
              <Card.Body className="p-0">
                {sidebarSectionsLoading ? (
                  <div className="text-center py-3 small text-muted">Loading sections...</div>
                ) : sidebarSections.length === 0 ? (
                  <div className="text-center py-4 px-3">
                    <i className="bi bi-folder2-open text-muted mb-2" style={{ fontSize: '1.75rem' }} />
                    <p className="small text-muted mb-2">No sections yet</p>
                    <p className="small text-muted mb-0">Sections will appear here for quick links, documents, and notes.</p>
                    {canDirectorActions && (
                      <Button size="sm" variant="outline-secondary" className="mt-3" onClick={() => { setSidebarSectionForm({ sectionId: null, name: '', direction: '', itemType: null }); setShowSidebarSectionModal(true); }}>
                        <i className="bi bi-plus-lg me-1" />New section
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {sidebarSections.map((sec) => (
                      <div key={sec.id} className="pb-3 border-dashed-bottom">
                        <div className="d-flex justify-content-between align-items-center mb-1 px-3 pt-2">
                          <h6 className="mb-0 mt-2 fw-semibold small text-break ">{sec.name || 'Section'}</h6>
                          {canDirectorActions && (
                            <Dropdown variant="link" size="sm" drop="down-centered">
                              <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted flex-shrink-0 no-arrow"><i className="bi bi-three-dots-vertical"></i></Dropdown.Toggle>
                              <Dropdown.Menu className="dropdown-menu-right">
                                <Dropdown.Item onClick={() => { setSidebarSectionForm({ sectionId: sec.id, name: sec.name || '', direction: sec.direction || '', itemType: sec.itemType }); setShowSidebarSectionModal(true); }}> <i className="bi bi-pencil me-1" /> Edit</Dropdown.Item>
                                  <Dropdown.Item onClick={() => {
                                    const next = sidebarSections.filter((s) => s.id !== sec.id);
                                    setSidebarSections(next);
                                    saveSidebarSections(next);
                                  }}> <i className="bi bi-trash me-1" /> Remove</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </div>
                        {sec.direction && <p className="small text-muted mb-2 px-3">{sec.direction}</p>}

                        {/* Section items: links list / documents list / notes carousel */}
                        {sec.itemType === 'link' && (
                          <ul className="list-unstyled small mb-2 resource-list-item">
                            {(sec.items || []).length === 0 ? (
                              <div  className="d-flex align-items-center flex-column justify-content-center px-3 py-2 text-muted small text-center">
                                <i className="bi bi-link-45deg d-block mb-1" style={{ fontSize: '1.25rem' }} />
                                No links yet
                              </div>
                            ) : (sec.items || []).map((item) => (
                              <li key={item.id} className="resource-container">
                                <a href={normalizeUrl(item.url)} target="_blank" rel="noopener noreferrer" className="d-flex flex-row justify-content-between align-items-center">
                                  {item.label || item.url}<i className={`bi bi-link-45deg p-0 me-1 ${canDirectorActions ? 'd-none' : 'd-block'}`}></i>
                                </a>
                                {canDirectorActions && (
                                  <Button variant="link-danger" size="sm" className="p-0 px-1 hide-button" onClick={() => {
                                    const next = sidebarSections.map((s) => {
                                      if (s.id !== sec.id) return s;
                                      return { ...s, items: (s.items || []).filter((i) => i.id !== item.id) };
                                    });
                                    setSidebarSections(next);
                                    saveSidebarSections(next);
                                  }}><i className="bi bi-x" /></Button>
                                )}
                              </li>
                            ))}
                            {canDirectorActions && (
                              <div className="mt-3 px-3">
                                <Button size="sm" variant="link-dashed" className="w-100" onClick={() => { setSidebarLinkForm({ sectionId: sec.id, url: '', label: '' }); setShowSidebarLinkModal(true); }}><i className="bi bi-plus me-1" />Add link</Button>
                              </div>
                            )}
                          </ul>
                        )}

                        {sec.itemType === 'document' && (
                          <ul className="list-unstyled small mb-2 resource-list-item mb-2">
                            {(sec.items || []).length === 0 ? (
                              <div className="d-flex align-items-center flex-column justify-content-center px-3 py-2 text-muted small text-center">
                                <i className="bi bi-file-earmark" style={{ fontSize: '1.25rem' }} />
                                No documents yet
                              </div>
                            ) : (sec.items || []).map((item) => (
                              <li key={item.id} className="resource-container d-flex align-items-center gap-1">
                                <i className={`bi ${getFileIcon(item.fileName, item.mimeType)} flex-shrink-0`} style={{ fontSize: '1rem' }} />
                                <span className="text-truncate flex-grow-1">{item.alternateName || item.originalName || item.fileName}</span>
                                <Button size="sm" variant="light-solid" className="p-0 px-1 flex-shrink-0 hide-button" title="Download" onClick={() => downloadFile({ webPath: item.webPath, originalName: item.originalName || item.fileName })}><i className="bi bi-download" /></Button>
                                {canDirectorActions && (
                                  <Button variant="link-danger" size="sm" className="p-0 px-1 flex-shrink-0 hide-button" onClick={() => {
                                    const next = sidebarSections.map((s) => {
                                      if (s.id !== sec.id) return s;
                                      return { ...s, items: (s.items || []).filter((i) => i.id !== item.id) };
                                    });
                                    setSidebarSections(next);
                                    saveSidebarSections(next);
                                  }}><i className="bi bi-x" /></Button>
                                )}
                              </li>
                            ))}
                            {canDirectorActions && (
                              <div className="mt-3 px-3">
                                <Button size="sm" variant="link-dashed" className="w-100" onClick={() => { setSidebarDocForm({ sectionId: sec.id, file: null, alternateName: '' }); setShowSidebarDocModal(true); }}><i className="bi bi-plus me-1" />Add document</Button>
                              </div>
                            )}
                          </ul>
                        )}

                        {sec.itemType === 'note' && (
                          <div>
                            {(sec.items || []).length === 0 ? (
                              <div className="px-3 py-2 text-muted small text-center">
                                <i className="bi bi-sticky d-block mb-1" style={{ fontSize: '1.25rem' }} />
                                No notes yet
                              </div>
                            ) : (
                              <>
                              <div className="small px-3 mb-2">
                                {(() => {
                                  const currentNote = (sec.items || [])[(sidebarNoteIndex[sec.id] ?? 0)];
                                  const variant = currentNote ? getNoteAlertVariant(currentNote.color) : 'warning';
                                  return (
                                    <Alert variant={variant} className="py-2 px-2 mb-2" style={{ fontSize: '0.8rem' }}>
                                      {currentNote?.text}
                                    </Alert>
                                  );
                                })()}
                                {/*<div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                                  <Button size="sm" variant="outline-secondary" className="p-1" onClick={() => setSidebarNoteIndex((p) => ({ ...p, [sec.id]: Math.max(0, (p[sec.id] ?? 0) - 1) }))}><i className="bi bi-chevron-left" /></Button>
                                  <span className="small text-muted">{(sidebarNoteIndex[sec.id] ?? 0) + 1} / {(sec.items || []).length}</span>
                                  <Button size="sm" variant="outline-secondary" className="p-1" onClick={() => setSidebarNoteIndex((p) => ({ ...p, [sec.id]: Math.min((sec.items || []).length - 1, (p[sec.id] ?? 0) + 1) }))}><i className="bi bi-chevron-right" /></Button>
                                </div>*/}
                                <div className="d-flex justify-content-center gap-1 mt-2 flex-wrap">
                                  {(sec.items || []).map((item, idx) => {
                                    const isActive = idx === (sidebarNoteIndex[sec.id] ?? 0);
                                    const map = { blue: 'primary', yellow: 'warning', red: 'danger', purple: 'info' };
                                    const resolved = map[item.color] ?? item.color ?? 'warning';
                                    const dotColor = !isActive ? 'bg-light-solid' : resolved === 'primary' ? 'bg-primary-solid' : resolved === 'warning' ? 'bg-warning-solid' : resolved === 'danger' ? 'bg-danger-solid' : 'bg-info-solid';
                                    return (
                                      <button key={idx} type="button" className={`rounded-circle border-0 p-1 ${dotColor}`} style={{ width: 8, height: 8 }} onClick={() => setSidebarNoteIndex((p) => ({ ...p, [sec.id]: idx }))} aria-label={`Note ${idx + 1}`} />
                                    );
                                  })}
                                </div>
                                </div>
                                {canDirectorActions && (
                                  (sec.items || []).map((item, idx) => (
                                    <ul key={item.id} className="list-unstyled mb-0 resource-list-item">
                                      <li> 
                                      <span className="text-truncate flex-grow-1 small">{(item.text || '').slice(0, 40)}...</span>
                                      <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => {
                                        const newItems = (sec.items || []).filter((i) => i.id !== item.id);
                                        const next = sidebarSections.map((s) => s.id === sec.id ? { ...s, items: newItems } : s);
                                        setSidebarSections(next);
                                        saveSidebarSections(next);
                                        setSidebarNoteIndex((p) => ({ ...p, [sec.id]: Math.min(p[sec.id] ?? 0, Math.max(0, newItems.length - 1)) }));
                                      }}><i className="bi bi-x" /></Button>
                                      </li>
                                    </ul>
                                  ))
                                )}
                              </>
                                )}
                                {canDirectorActions && (
                              <div className="mt-3 px-3">
                                <Button size="sm" variant="link-dashed" className="w-100" onClick={() => { setSidebarNoteForm({ sectionId: sec.id, text: '', color: 'warning' }); setShowSidebarNoteModal(true); }}><i className="bi bi-plus me-1" />Add note</Button>
                              </div>
                            )}
                          </div>
                        )}


                        {/* Director: choose type when section has no type yet (e.g. legacy) */}
                        {canDirectorActions && !sec.itemType && (
                          <div className="d-flex gap-1 flex-wrap mb-2">
                            <Button size="sm" variant="outline-primary" onClick={() => { setSidebarSectionForm({ sectionId: sec.id, name: sec.name || '', direction: sec.direction || '', itemType: 'link' }); setShowSidebarSectionModal(true); }}>Links</Button>
                            <Button size="sm" variant="outline-info" onClick={() => { setSidebarSectionForm({ sectionId: sec.id, name: sec.name || '', direction: sec.direction || '', itemType: 'document' }); setShowSidebarSectionModal(true); }}>Documents</Button>
                            <Button size="sm" variant="outline-warning" onClick={() => { setSidebarSectionForm({ sectionId: sec.id, name: sec.name || '', direction: sec.direction || '', itemType: 'note' }); setShowSidebarSectionModal(true); }}>Notes</Button>
                          </div>
                        )}
                      </div>
                    ))}

                    {canDirectorActions && (
                      <div className="mt-2 px-3">
                        <Button size="sm" variant="link-dashed" className="w-100" disabled={sidebarSectionsSaving} onClick={() => { setSidebarSectionForm({ sectionId: null, name: '', direction: '', itemType: null }); setShowSidebarSectionModal(true); }}><i className="bi bi-plus-lg me-1" />New section</Button>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Resource Modal */}
      <Modal 
        show={showResourceModal} 
        onHide={() => {
          setShowResourceModal(false);
          resetResourceForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            {editingResource ? (
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Edit Resource</span>
                <div className="text-hl"></div>
              </div>
            ) : !showTypeSelection && resourceForm.type ? (
              <div className="d-flex align-items-center">
                <Button
                  variant="secondary"
                  size="sm"
                  className="me-2"
                  onClick={() => setShowTypeSelection(true)}
                >
                  <i className="bi bi-arrow-left"></i>
                </Button>
                <div className="d-flex align-items-center">
                  {resourceForm.type === 'link' && (
                    <>
                      <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-2">
                        <i className="bi bi-link-45deg text-primary"></i>
                      </div>
                      <span className="fw-semibold">Link Resource</span>
                    </>
                  )}
                  {resourceForm.type === 'document' && (
                    <>
                      <div className="bg-info bg-opacity-10 rounded-3 p-2 me-2">
                        <i className="bi bi-file-earmark text-info"></i>
                      </div>
                      <span className="fw-semibold">Document Resource</span>
                    </>
                  )}
                  {resourceForm.type === 'note' && (
                    <>
                      <div className="bg-warning bg-opacity-10 rounded-3 p-2 me-2">
                        <i className="bi bi-sticky-fill text-warning"></i>
                      </div>
                      <span className="fw-semibold">Note Resource</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Add Resource</span>
                <div className="text-hl"></div>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {resourceError && (
            <Alert variant="danger" onClose={() => setResourceError('')} dismissible>
              {resourceError}
            </Alert>
          )}
          
          {!editingResource && showTypeSelection ? (
            // Type Selection Step
            <div className="px-5">
              <h6 className="mb-4 text-center fw-semibold">Select Resource Type</h6>
              <Row className="g-3">
                <Col md={4}>
                  <Card 
                    className="shadow-sm border-1 h-100 text-center cursor-pointer"
                    style={{ 
                      cursor: 'pointer',
                      borderColor: resourceForm.type === 'link' ? 'var(--bs-blue)' : 'var(--border-bg)',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleTypeSelection('link')}
                    onMouseEnter={(e) => {
                      if (resourceForm.type !== 'link') {
                        e.currentTarget.style.borderColor = 'var(--bs-primary)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (resourceForm.type !== 'link') {
                        e.currentTarget.style.borderColor = 'var(--border-bg)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <Card.Body className="px-3 py-4">
                      <div className="bg-primary bg-opacity-10 rounded-3 px-3 py-2 mb-3 d-inline-block">
                        <i className="bi bi-link-45deg text-primary" style={{ fontSize: '1.75rem' }}></i>
                      </div>
                      <h5 className="mb-2 fw-semibold">Link</h5>
                      <p className="text-muted small mb-0">Add a helpful web link or URL</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card 
                    className="shadow-sm border-1 h-100 text-center cursor-pointer"
                    style={{ 
                      cursor: 'pointer',
                      borderColor: resourceForm.type === 'document' ? 'var(--indigo)' : 'var(--border-bg)',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleTypeSelection('document')}
                    onMouseEnter={(e) => {
                      if (resourceForm.type !== 'document') {
                        e.currentTarget.style.borderColor = 'var(--bs-info)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (resourceForm.type !== 'document') {
                        e.currentTarget.style.borderColor = 'var(--border-bg)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <Card.Body className="px-3 py-4">
                      <div className="bg-info bg-opacity-10 rounded-3 px-3 py-2 mb-3 d-inline-block">
                        <i className="bi bi-file-earmark text-info" style={{ fontSize: '1.75rem' }}></i>
                      </div>
                      <h5 className="mb-2 fw-semibold">Document</h5>
                      <p className="text-muted small mb-0">Upload a file or document</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card 
                    className="shadow-sm border-1 h-100 text-center cursor-pointer"
                    style={{ 
                      cursor: 'pointer',
                      borderColor: resourceForm.type === 'note' ? 'var(--yellow)' : 'var(--border-bg)',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleTypeSelection('note')}
                    onMouseEnter={(e) => {
                      if (resourceForm.type !== 'note') {
                        e.currentTarget.style.borderColor = 'var(--yellow)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (resourceForm.type !== 'note') {
                        e.currentTarget.style.borderColor = 'var(--border-bg)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <Card.Body className="px-3 py-4">
                      <div className="bg-warning bg-opacity-10 rounded-3 px-3 py-2 mb-3 d-inline-block">
                        <i className="bi bi-sticky-fill text-warning" style={{ fontSize: '1.75rem' }}></i>
                      </div>
                      <h5 className="mb-2 fw-semibold">Note</h5>
                      <p className="text-muted small mb-0">Write a helpful note or information</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            // Form Step
            <Form>

              {editingResource && (
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="resource-type" label="Type" className="required-field">
                    <Form.Select
                      value={resourceForm.type}
                      onChange={(e) => setResourceForm({...resourceForm, type: e.target.value, link: '', note: '', noteColor: 'warning', bulkLinks: ''})}
                      required
                    >
                      <option value="link">Link</option>
                      <option value="document">Document</option>
                      <option value="note">Note</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              )}
              <Row>
                <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="resource-title" label="Title" className="required-field">
                  <Form.Control
                    type="text"
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                    required
                    placeholder="Enter resource title"
                  />
                </FloatingLabel>
              </Form.Group>
              </Col>
              <Col md={6}>
              <Form.Group className="mb-3">
              <FloatingLabel controlId="resource-keywords" label="Keywords/Hashtags (comma-separated)">
                <Form.Control
                  type="text"
                  value={resourceForm.keywords}
                  onChange={(e) => setResourceForm({...resourceForm, keywords: e.target.value})}
                  placeholder="surgery, procedures, guidelines"
                />
              </FloatingLabel>
            </Form.Group>
              </Col>
              </Row>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="resource-description" label="Description (max 100 characters)">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={resourceForm.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setResourceForm({...resourceForm, description: value});
                      }
                    }}
                    placeholder="Enter a brief description of this resource"
                  />
                </FloatingLabel>
                <Form.Text className="text-muted">
                  {resourceForm.description.length}/100 characters
                </Form.Text>
              </Form.Group>

            {resourceForm.type === 'link' && (
              <>
                  <Form.Group className="mb-3">
                  <FloatingLabel controlId="resource-content-link" label="Content">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={resourceForm.content}
                      onChange={(e) => setResourceForm({...resourceForm, content: e.target.value})}
                      placeholder="Enter additional content or notes about these links"
                      style={{minHeight: '100px'}}
                    />
                  </FloatingLabel>
                </Form.Group>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="resource-bulk-links" label="Links (one per line)" className="required-field">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={resourceForm.bulkLinks}
                      onChange={(e) => setResourceForm({...resourceForm, bulkLinks: e.target.value})}
                      placeholder="https://example.com&#10;https://another-link.com"
                      required
                      style={{minHeight: '120px'}}
                    />
                  </FloatingLabel>
                  <Form.Text className="text-muted">
                    Enter one or more links, one per line. All links will be stored in a single resource entry.
                  </Form.Text>
                </Form.Group>

              </>
            )}

            {resourceForm.type === 'document' && (
              <>
                              <Form.Group className="mb-3">
                  <FloatingLabel controlId="resource-content-document" label="Content">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={resourceForm.content}
                      onChange={(e) => setResourceForm({...resourceForm, content: e.target.value})}
                      placeholder="Enter additional content or notes about this document"
                      style={{minHeight: '100px'}}
                    />
                  </FloatingLabel>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Upload Files</Form.Label>
                  
                  {/* Show existing files when editing */}
                  {editingResource && existingFiles.length > 0 && (
                    <div className="mb-3">
                      <Form.Label className="small text-muted">Current Files:</Form.Label>
                      {existingFiles
                        .filter(file => !removedFiles.some(removed => 
                          removed.fileName === file.fileName || removed.webPath === file.webPath
                        ))
                        .map((file, idx) => (
                          <div key={idx} className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2">
                            <div className="d-flex align-items-center">
                              <i className={`bi ${getFileIcon(file.fileName, file.mimeType)} me-2`}></i>
                              <div>
                                <small className="d-block">{file.originalName || file.fileName}</small>
                                {file.fileSize && (
                                  <small className="text-muted">{formatFileSize(file.fileSize)}</small>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setRemovedFiles([...removedFiles, file]);
                              }}
                              title="Remove file"
                            >
                              <i className="bi bi-x-lg"></i>
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  <Form.Control
                    id="resource-file-input"
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        if (!resourceForm.title) {
                          setResourceForm({...resourceForm, title: resourceForm.title || e.target.files[0].name});
                        }
                      }
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.zip"
                  />
                  <Form.Text className="text-muted">
                    {editingResource 
                      ? 'Select additional files to add to this resource.'
                      : 'Select one or more files to upload. You can select multiple files at once.'}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {resourceForm.type === 'note' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="required-field">Note Content</Form.Label>
                  <RichTextEditor
                    value={resourceForm.note}
                    onChange={(html) =>
                      setResourceForm((prev) => ({ ...prev, note: html }))
                    }
                    placeholder="Enter note content..."
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="resource-note-color" label="Note color">
                    <Form.Select
                      value={resourceForm.noteColor || 'warning'}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, noteColor: e.target.value }))}
                    >
                      <option value="primary">Blue</option>
                      <option value="warning">Yellow</option>
                      <option value="danger">Red</option>
                      <option value="info">Purple</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </>
            )}

            {/*<Form.Group className="mb-3">
              <FloatingLabel controlId="resource-keywords" label="Keywords/Hashtags (comma-separated)">
                <Form.Control
                  type="text"
                  value={resourceForm.keywords}
                  onChange={(e) => setResourceForm({...resourceForm, keywords: e.target.value})}
                  placeholder="surgery, procedures, guidelines"
                />
              </FloatingLabel>
              <Form.Text className="text-muted">
                Add keywords or hashtags separated by commas to help others find this resource easily.
              </Form.Text>
            </Form.Group>*/}
          </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          {!editingResource && showTypeSelection ? (
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => {
                setShowResourceModal(false);
                resetResourceForm();
              }}
            >
              Cancel
            </Button>
          ) : (
            <>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => {
                  setShowResourceModal(false);
                  resetResourceForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleResourceSubmit}
                disabled={isSubmittingResource}
              >
                {isSubmittingResource ? 'Saving...' : (editingResource ? 'Update' : 'Create')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Delete Resource</span>
              <div className="text-hl-danger"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {confirmMessage}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="secondary" size="sm" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={async () => { setShowConfirmModal(false); await confirmAction(); }}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sidebar: Add Link Modal */}
      <Modal show={showSidebarLinkModal} onHide={() => { setShowSidebarLinkModal(false); setSidebarLinkForm({ sectionId: null, url: '', label: '' }); }} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <div className="d-flex flex-column justify-content-start align-items-start w-100">
            <h6 className="mb-0 fw-semibold">Add link to section</h6>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <FloatingLabel controlId="sidebar-link-url" label="URL" className="mb-2">
            <Form.Control
              type="url"
              placeholder="https://..."
              value={sidebarLinkForm.url}
              onChange={(e) => setSidebarLinkForm((p) => ({ ...p, url: e.target.value }))}
            />
          </FloatingLabel>
          <FloatingLabel controlId="sidebar-link-label" label="Label (optional)">
            <Form.Control
              type="text"
              placeholder="Display text"
              value={sidebarLinkForm.label}
              onChange={(e) => setSidebarLinkForm((p) => ({ ...p, label: e.target.value }))}
            />
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer className=" border-top-0">
          <Button variant="secondary" size="sm" onClick={() => { setShowSidebarLinkModal(false); setSidebarLinkForm({ sectionId: null, url: '', label: '' }); }}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => {
            if (!sidebarLinkForm.url.trim()) return;
            const secId = sidebarLinkForm.sectionId;
            const next = sidebarSections.map((s) => s.id !== secId ? s : { ...s, itemType: 'link', items: [...(s.items || []), { id: genId(), url: sidebarLinkForm.url.trim(), label: sidebarLinkForm.label.trim() || undefined }] });
            setSidebarSections(next);
            saveSidebarSections(next);
            setShowSidebarLinkModal(false);
            setSidebarLinkForm({ sectionId: null, url: '', label: '' });
          }}>Add link</Button>
        </Modal.Footer>
      </Modal>

      {/* Sidebar: Add Note Modal */}
      <Modal show={showSidebarNoteModal} onHide={() => { setShowSidebarNoteModal(false); setSidebarNoteForm({ sectionId: null, text: '', color: 'warning' }); }} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <div className="d-flex flex-column justify-content-start align-items-start w-100">
            <h6 className="mb-0 fw-semibold">Add note to section</h6>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <FloatingLabel controlId="sidebar-note-text" label="Note text" className="mb-3">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter note content..."
              value={sidebarNoteForm.text}
              onChange={(e) => setSidebarNoteForm((p) => ({ ...p, text: e.target.value }))}
            />
          </FloatingLabel>
          <Form.Group className="mb-0">
            <Form.Label className="small fw-semibold">Alert color</Form.Label>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {[
                { id: 'blue', label: 'Blue', variant: 'Primary' },
                { id: 'yellow', label: 'Yellow', variant: 'Warning' },
                { id: 'red', label: 'Red', variant: 'Danger' },
                { id: 'purple', label: 'Purple', variant: 'Info' },
              ].map(({ id, label, variant }) => (
                <Button
                  key={id}
                  size="sm"
                  variant={sidebarNoteForm.color === id ? variant : `outline-${variant}`}
                  className={sidebarNoteForm.color === id ? 'btn-'+variant.toLowerCase() : 'btn-outline-' + variant.toLowerCase()}
                  onClick={() => setSidebarNoteForm((p) => ({ ...p, color: id }))}
                >
                  {label}
                </Button>
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className=" border-top-0">
          <Button variant="outline-secondary" size="sm" onClick={() => { setShowSidebarNoteModal(false); setSidebarNoteForm({ sectionId: null, text: '', color: 'warning' }); }}>Cancel</Button>
          <Button variant="warning" size="sm" onClick={() => {
            if (!sidebarNoteForm.text.trim()) return;
            const secId = sidebarNoteForm.sectionId;
            const next = sidebarSections.map((s) => s.id !== secId ? s : { ...s, itemType: 'note', items: [...(s.items || []), { id: genId(), text: sidebarNoteForm.text.trim(), color: sidebarNoteForm.color || 'warning' }] });
            setSidebarSections(next);
            saveSidebarSections(next);
            setShowSidebarNoteModal(false);
            setSidebarNoteForm({ sectionId: null, text: '', color: 'warning' });
          }}>Add note</Button>
        </Modal.Footer>
      </Modal>

      {/* Sidebar: Add Document Modal */}
      <Modal show={showSidebarDocModal} onHide={() => { if (!sidebarDocUploading) { setShowSidebarDocModal(false); setSidebarDocForm({ sectionId: null, file: null, alternateName: '' }); } }} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <div className="d-flex flex-column justify-content-start align-items-start w-100">
            <h6 className="mb-0 fw-semibold">Add document to section</h6>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">File</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => setSidebarDocForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.zip"
            />
            {sidebarDocForm.file && <Form.Text className="text-muted">Selected: {sidebarDocForm.file.name}</Form.Text>}
          </Form.Group>
          <FloatingLabel controlId="sidebar-doc-display-name" label="Display name (optional)">
            <Form.Control
              type="text"
              placeholder="Leave blank to use file name"
              value={sidebarDocForm.alternateName}
              onChange={(e) => setSidebarDocForm((p) => ({ ...p, alternateName: e.target.value }))}
            />
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer className=" border-top-0">
          <Button variant="outline-secondary" size="sm" onClick={() => { if (!sidebarDocUploading) { setShowSidebarDocModal(false); setSidebarDocForm({ sectionId: null, file: null, alternateName: '' }); } }}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!sidebarDocForm.file || sidebarDocUploading} onClick={async () => {
            if (!sidebarDocForm.file) return;
            setSidebarDocUploading(true);
            try {
              const token = localStorage.getItem('token');
              const fd = new FormData();
              fd.append('file', sidebarDocForm.file);
              const res = await fetch(`${API_ENDPOINTS.RESOURCES}/sidebar-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
              if (!res.ok) throw new Error('Upload failed');
              const data = await res.json();
              const secId = sidebarDocForm.sectionId;
              const next = sidebarSections.map((s) => s.id !== secId ? s : { ...s, itemType: 'document', items: [...(s.items || []), { id: genId(), ...data, alternateName: sidebarDocForm.alternateName.trim() || undefined }] });
              setSidebarSections(next);
              saveSidebarSections(next);
              setShowSidebarDocModal(false);
              setSidebarDocForm({ sectionId: null, file: null, alternateName: '' });
              toast.success('Document added');
            } catch (err) {
              toast.error('Upload failed');
            } finally {
              setSidebarDocUploading(false);
            }
          }}>{sidebarDocUploading ? 'Uploading...' : 'Add document'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Sidebar: Section (Add / Edit) Modal - name, direction, and type */}
      <Modal show={showSidebarSectionModal} onHide={() => { setShowSidebarSectionModal(false); setSidebarSectionForm({ sectionId: null, name: '', direction: '', itemType: null }); }} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <div className="d-flex flex-column justify-content-start align-items-start w-100">
            <h6 className="mb-0 fw-semibold">{sidebarSectionForm.sectionId ? 'Edit section' : 'Add section'}</h6>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
          <FloatingLabel controlId="sidebar-section-name" label="Section name" className="mb-3">
            <Form.Control
              placeholder="e.g. Quick links"
              value={sidebarSectionForm.name}
              onChange={(e) => setSidebarSectionForm((p) => ({ ...p, name: e.target.value }))}
            />
          </FloatingLabel>
          </Col>
          <Col md={4}>          
          <FloatingLabel controlId="sidebar-section-type" label="Section type" className="mb-3">
                <Form.Select
                  value={sidebarSectionForm.itemType}
                  onChange={(e) => setSidebarSectionForm((p) => ({ ...p, itemType: e.target.value }))}
                >
                  <option value="link">Links</option>
                  <option value="document">Documents</option>
                  <option value="note">Notes</option>
              </Form.Select>
          </FloatingLabel>
          </Col>
          <Col md={12}>
          <FloatingLabel controlId="sidebar-section-direction" label="Direction (optional)">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Brief instruction or description"
              value={sidebarSectionForm.direction}
              onChange={(e) => setSidebarSectionForm((p) => ({ ...p, direction: e.target.value }))}
            />
          </FloatingLabel>
          </Col>
          </Row>
          
        </Modal.Body>
        <Modal.Footer className=" border-top-0">
          <Button variant="outline-secondary" size="sm" onClick={() => { setShowSidebarSectionModal(false); setSidebarSectionForm({ sectionId: null, name: '', direction: '', itemType: null }); }}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={sidebarSectionForm.sectionId ? false : !sidebarSectionForm.itemType} onClick={() => {
            const { sectionId, name, direction, itemType } = sidebarSectionForm;
            if (sectionId) {
              const next = sidebarSections.map((s) => s.id !== sectionId ? s : { ...s, name: name.trim() || s.name, direction: direction.trim() || '', ...(itemType ? { itemType, items: s.items || [] } : {}) });
              setSidebarSections(next);
              saveSidebarSections(next);
            } else {
              if (!itemType) return;
              const next = [...sidebarSections, { id: genId(), name: name.trim() || 'Section', direction: direction.trim() || '', itemType, items: [] }];
              setSidebarSections(next);
              saveSidebarSections(next);
            }
            setShowSidebarSectionModal(false);
            setSidebarSectionForm({ sectionId: null, name: '', direction: '', itemType: null });
          }}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResourcesPage;

